/**
 * UNIFIED CONTRACT DATA HOOK
 * Single source of truth for all contract data across all user roles
 *
 * This replaces all the fragmented contract data fetching and ensures
 * consistent data structure and clause extraction for all users.
 *
 * Wave 2.5: optionally MERGES clause-dictionary entries (additive).
 * Hardcoded residential extractors are never removed.
 */

import { useMemo } from 'react';

import { useResolveClauseDictionaryQuery } from '@/apis/extended/clauseDictionaryApi';
import { useGetContractQuery } from '@/apis/extended/contractApi';
import { useGetContractProposalsQuery } from '@/apis/extended/proposalApi';
import {
  ComprehensiveClauseCategory,
  extractComprehensiveContractClausesWithIds,
} from '@/utils/comprehensiveClauseMapping';
import { mergeDictionaryIntoCategories } from '@/utils/mergeDictionaryClauses';
import { mergeProposalsIntoClauses } from '@/utils/mergeProposalsIntoClauses';
import { Contract } from '@/types/ContractType';

interface ContractDataResult {
  contract: Contract | undefined;
  isLoading: boolean;
  error: any;
  isRatified: boolean;
  canNegotiate: boolean;
  comprehensiveClauses: ComprehensiveClauseCategory[];
  refetch: () => void;
}

function contractState(contract: Contract | undefined): string | undefined {
  if (!contract) return undefined;
  const raw =
    (contract as any).sellerPropertyInfo?.propertyState ||
    (contract as any).agentSellerInfo?.propertyState ||
    (contract as any).buyerInfo?.state ||
    '';
  return String(raw || '').trim() || undefined;
}

function contractPropertyType(
  contract: Contract | undefined
): string | undefined {
  if (!contract) return undefined;
  const raw =
    (contract as any).sellerPropertyInfo?.propertyType ||
    (contract as any).agentSellerInfo?.propertyType ||
    '';
  return String(raw || '').trim() || undefined;
}

export const useContractData = (
  contractId: string,
  userRole?: 'buyer' | 'seller' | 'agent'
): ContractDataResult => {
  const {
    data: contract,
    isLoading: contractLoading,
    error: contractError,
    refetch: refetchContract,
  } = useGetContractQuery(contractId, {
    skip: !contractId,
  });

  const {
    data: proposalsData,
    isLoading: proposalsLoading,
    refetch: refetchProposals,
  } = useGetContractProposalsQuery({ contractId }, { skip: !contractId });

  const state = contractState(contract);
  const propertyType = contractPropertyType(contract);
  const isCommercial = propertyType != null && /commercial/i.test(propertyType);

  const {
    data: dictionary = [],
    isLoading: dictionaryLoading,
    refetch: refetchDictionary,
  } = useResolveClauseDictionaryQuery(
    {
      state,
      propertyType: propertyType || (isCommercial ? 'Commercial' : undefined),
      contractId,
    },
    {
      // Always allow deal-scoped proposes; for residential SFH global pack may be empty.
      skip: !contractId,
    }
  );

  const proposals = proposalsData?.proposals ?? [];

  const isRatified = contract ? isContractRatified(contract) : false;
  const canNegotiate = contract ? !isRatified : false;

  const comprehensiveClauses = useMemo(() => {
    if (!contract || !contractId) return [];

    const base = extractComprehensiveContractClausesWithIds(
      contract,
      contractId
    );

    // Additive merge — never strips base residential clauses
    const withDict = mergeDictionaryIntoCategories(
      base,
      dictionary,
      contractId
    );

    if (userRole && proposals.length > 0) {
      return mergeProposalsIntoClauses(
        withDict,
        proposals as unknown as Parameters<typeof mergeProposalsIntoClauses>[1],
        userRole
      );
    }
    return withDict;
  }, [contract, contractId, dictionary, proposals, userRole]);

  const refetch = () => {
    refetchContract();
    refetchProposals();
    refetchDictionary();
  };

  return {
    contract,
    isLoading: contractLoading || proposalsLoading || dictionaryLoading,
    error: contractError,
    isRatified,
    canNegotiate,
    comprehensiveClauses,
    refetch,
  };
};

function isContractRatified(contract: Contract): boolean {
  if (!contract) return false;

  if ((contract as any).isRatified === true) return true;
  if ((contract as any).contractStatus === 'ratified') return true;

  const buyerSigned = Boolean(
    contract.buyerSign?.isStored || (contract.buyerSign as any)?.signature
  );
  const sellerSigned = Boolean(
    (contract as any).sellerSign?.isStored ||
      (contract as any).sellerSign?.signature
  );

  return buyerSigned && sellerSigned;
}
